import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Separator } from "@/components/ui/separator";
import { Mail, Phone, MapPin, Linkedin, Facebook } from "lucide-react";

const contactItems = [
  {
    icon: Mail,
    label: "Email Us",
    value: "raihan@raihaninternational.com",
    href: "mailto:raihan@raihaninternational.com",
  },
  {
    icon: Phone,
    label: "Call Us",
    value: "+91 7003663747",
    href: "tel:+917003663747",
  },
  {
    icon: MapPin,
    label: "Visit Us",
    value: "Kolkata, West Bengal, India",
    href: null,
  },
];

const socials = [
  {
    icon: Linkedin,
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/raihaninternational/",
  },
  {
    icon: Facebook,
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61577806037880",
  },
];

export default function Contact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#1A1614]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 space-y-4"
        >
          <p className="font-accent text-[#D4AF37] text-lg" data-testid="contact-accent">
            Get in touch
          </p>
          <h2
            data-testid="contact-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Contact Us
          </h2>
          <Separator className="w-16 h-0.5 bg-[#D4AF37] mx-auto" />
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {contactItems.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`contact-item-${i}`}
              className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center hover:bg-white/10 transition-all duration-300 group"
            >
              <div className="w-14 h-14 rounded-full bg-[#8D5A34]/20 flex items-center justify-center mx-auto mb-5 group-hover:bg-[#8D5A34]/30 transition-colors">
                <item.icon size={24} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <h3
                className="text-white text-lg font-semibold mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {item.label}
              </h3>
              {item.href ? (
                <a
                  href={item.href}
                  data-testid={`contact-link-${i}`}
                  className="text-white/60 text-sm hover:text-[#D4AF37] transition-colors"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {item.value}
                </a>
              ) : (
                <p
                  className="text-white/60 text-sm"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {item.value}
                </p>
              )}
            </motion.div>
          ))}
        </div>

        {/* Social links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex items-center justify-center gap-4 mt-12"
          data-testid="contact-socials"
        >
          <span
            className="text-white/40 text-sm mr-2"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Follow us:
          </span>
          {socials.map((s, i) => (
            <a
              key={i}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`contact-social-${s.label.toLowerCase()}`}
              className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition-all duration-300"
            >
              <s.icon size={18} strokeWidth={1.5} />
            </a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
